import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DEMO_VOORWAARDEN, DEMO_VOORWAARDEN_TITEL } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';

const DEV_PERSONAS = [
  {
    token: 'dev-admin-token',
    label: 'Linda van den Berg',
    role: 'SUPPOHRT Admin',
    tenant: 'suppohrt',
  },
  {
    token: 'dev-medewerker-token',
    label: 'Dev POH-GGZ',
    role: 'SUPPOHRT Medewerker',
    tenant: 'suppohrt',
  },
  {
    token: 'dev-kantoor-token',
    label: 'Dev Kantoor',
    role: 'Kantoorpersoneel',
    tenant: 'suppohrt',
  },
  {
    token: 'dev-zorggroep-token',
    label: 'RHOGO POH',
    role: 'Zorggroep Gebruiker',
    tenant: 'rhogo',
  },
  {
    token: 'dev-zorggroep-admin-token',
    label: 'RHOGO Admin',
    role: 'Zorggroep Admin',
    tenant: 'rhogo',
  },
];

const ERROR_MESSAGES: Record<string, string> = {
  missing_params: 'Inloggen mislukt. Probeer opnieuw.',
  missing_state: 'De inlogsessie is verlopen. Probeer opnieuw.',
  state_mismatch: 'Veiligheidsfout tijdens inloggen. Probeer opnieuw.',
  token_exchange: 'Microsoft accepteerde de inlog niet. Probeer opnieuw.',
  id_token_invalid: 'Microsoft-token kon niet geverifieerd worden.',
  no_app_account:
    'Je hebt nog geen toegang tot deze Kennishub. Vraag de IT-beheerder om je in de juiste groep te plaatsen.',
  provision_failed: 'Account aanmaken mislukt. Probeer opnieuw of neem contact op met een admin.',
  deactivated: 'Je account is gedeactiveerd. Neem contact op met een admin.',
  nog_niet_open:
    'De Kennishub is voor jouw organisatie nog niet geopend. Je krijgt bericht zodra je kunt inloggen.',
  demo_access_ended:
    'Je toegang tot de demo is verlopen of beëindigd. Neem contact op met SUPPOHRT als je nog eens wilt kijken.',
};

export function LoginPage(): JSX.Element {
  const { setDevToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Een gedeelde link (bv. /agenda?item=<id>) moet na het inloggen alsnog
  // openen; RequireAuth geeft de oorspronkelijke locatie mee als state.
  const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
  const returnTo = from ? from.pathname + from.search : '';

  const errorKey = new URLSearchParams(location.search).get('error');
  const errorMsg = errorKey ? (ERROR_MESSAGES[errorKey] ?? `Onbekende fout: ${errorKey}`) : null;

  const devTokensEnabled = import.meta.env.DEV;

  // Demo-omgeving (issue #39): bestaat de demo-login op deze deployment, dan
  // vervangt een toegangscodeveld de Microsoft-knop. NIET achter
  // import.meta.env.DEV — dit moet juist in de productiebuild van de
  // demo-service werken; op productie geeft de check gewoon 404.
  const [demoMode, setDemoMode] = useState(false);
  const [demoCode, setDemoCode] = useState('');
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);
  // Eerste login met een code: de server vraagt eerst akkoord (409
  // terms_required); pas dan tonen we de voorwaarden met het vinkje.
  const [toonVoorwaarden, setToonVoorwaarden] = useState(false);
  const [akkoord, setAkkoord] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/v1/auth/demo', { credentials: 'include' })
      .then((res) => {
        if (!cancelled && res.status === 204) setDemoMode(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function submitDemo(e: FormEvent): Promise<void> {
    e.preventDefault();
    setDemoBusy(true);
    setDemoError(null);
    try {
      const res = await fetch('/api/v1/auth/demo/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: demoCode.trim(),
          ...(toonVoorwaarden ? { akkoordVoorwaarden: akkoord } : {}),
        }),
      });
      if (res.status === 204) {
        // Harde navigatie zodat AuthProvider /api/me opnieuw ophaalt met de
        // verse sessiecookie (zelfde primitief als signOut).
        window.location.assign('/');
        return;
      }
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      if (res.status === 409 && body?.error === 'terms_required') {
        setToonVoorwaarden(true);
        if (toonVoorwaarden) setDemoError('Vink aan dat je akkoord gaat met de voorwaarden.');
      } else if (res.status === 403 && body?.message) {
        // Verlopen of ingetrokken: de server formuleert de melding.
        setDemoError(body.message);
      } else if (res.status === 401) {
        setDemoError(
          'Deze toegangscode kennen we niet. Controleer de code in je uitnodigingsmail of neem contact op met SUPPOHRT.',
        );
      } else if (res.status === 429) {
        setDemoError('Te veel pogingen. Wacht even en probeer het opnieuw.');
      } else {
        setDemoError('Inloggen mislukt. Probeer het later opnieuw.');
      }
    } catch {
      setDemoError('Inloggen mislukt. Controleer je verbinding en probeer opnieuw.');
    } finally {
      setDemoBusy(false);
    }
  }

  function pickDev(token: string) {
    setDevToken(token);
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50/60 via-white to-brand-100/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-brand-100 bg-white p-8 shadow-card">
        <header className="mb-6 flex flex-col items-center text-center">
          <img src="/SUPPOHRT-logo-800px.webp" alt="SUPPOHRT" className="h-9 w-auto" />
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-ink-800/60">
            Kennishub
          </div>
          <h1 className="mt-2 font-display text-2xl font-bold text-ink-900">
            {demoMode ? 'Welkom bij de demo' : 'Welkom terug'}
          </h1>
          <p className="mt-1 text-sm text-ink-900/60">
            {demoMode
              ? 'Deze demo-omgeving toont de Kennishub met fictieve gegevens. Voer je persoonlijke toegangscode in om rond te kijken.'
              : 'Log in met je Microsoft-account om door te gaan.'}
          </p>
        </header>

        {errorMsg && !demoError && (
          <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMsg}
          </p>
        )}

        {demoMode ? (
          <form onSubmit={submitDemo} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-900">Toegangscode</span>
              <input
                type="text"
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="XXXX-XXXX-XXXX"
                value={demoCode}
                onChange={(e) => {
                  setDemoCode(e.target.value);
                  setToonVoorwaarden(false);
                  setAkkoord(false);
                }}
                className="w-full rounded-lg border border-brand-100 px-4 py-3 font-mono tracking-widest text-ink-900 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </label>
            {toonVoorwaarden && (
              <div className="rounded-lg border border-brand-100 bg-brand-50/60 p-4">
                <h2 className="text-sm font-bold text-ink-900">{DEMO_VOORWAARDEN_TITEL}</h2>
                <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-ink-900/80">
                  {DEMO_VOORWAARDEN.map((regel) => (
                    <li key={regel}>{regel}</li>
                  ))}
                </ol>
                <label className="mt-3 flex items-start gap-2 text-sm font-medium text-ink-900">
                  <input
                    type="checkbox"
                    checked={akkoord}
                    onChange={(e) => setAkkoord(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-brand-300 text-brand-600 focus:ring-brand-300"
                  />
                  <span>Ik ga akkoord</span>
                </label>
              </div>
            )}
            {demoError && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {demoError}
              </p>
            )}
            <button
              type="submit"
              disabled={demoBusy || demoCode.trim().length === 0 || (toonVoorwaarden && !akkoord)}
              className="flex w-full items-center justify-center rounded-lg bg-brand-600 px-4 py-3 font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-300 disabled:opacity-50"
            >
              {demoBusy ? 'Bezig met inloggen…' : 'Start de demo'}
            </button>
            <p className="text-xs text-ink-900/40">
              Alle gegevens in deze omgeving zijn fictief. In de echte Kennishub log je in met je
              Microsoft-account.
            </p>
          </form>
        ) : (
          /* Plain anchor — not a fetch — because the backend issues a 302 to
             login.microsoftonline.com which a fetch can't follow cross-origin.
             Full page navigation is the right primitive here. */
          <a
            href={`/api/v1/oauth/entra/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="flex w-full items-center justify-center gap-3 rounded-lg bg-brand-600 px-4 py-3 text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-300"
          >
            <MicrosoftLogo />
            <span className="font-medium">Inloggen met Microsoft</span>
          </a>
        )}

        {devTokensEnabled && (
          <>
            <div className="my-8 flex items-center gap-3 text-[11px] uppercase tracking-wider text-ink-800/40">
              <span className="h-px flex-1 bg-brand-100" />
              <span>Dev personas (alleen lokaal)</span>
              <span className="h-px flex-1 bg-brand-100" />
            </div>
            <div className="space-y-2">
              {DEV_PERSONAS.map((p) => (
                <button
                  key={p.token}
                  type="button"
                  onClick={() => pickDev(p.token)}
                  className="flex w-full items-center justify-between rounded-lg border border-brand-100 bg-white px-4 py-3 text-left transition hover:border-brand-300 hover:bg-brand-50/60 focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  <span>
                    <span className="block text-sm font-medium text-ink-900">{p.label}</span>
                    <span className="block text-xs text-ink-900/50">
                      {p.role} · tenant: {p.tenant}
                    </span>
                  </span>
                  <span className="text-ink-900/30">→</span>
                </button>
              ))}
            </div>
            <p className="mt-6 text-xs text-ink-900/40">
              Dev-tokens werken alleen in{' '}
              <code className="rounded bg-brand-50 px-1 text-ink-900/70">npm run dev</code> en zijn
              uitgeschakeld in de productiebuild.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function MicrosoftLogo(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
